import { useState, useEffect } from 'react'
import { contactAPI } from '../../services/api'

import { logger } from '@/utils/logger';
export default function MessagesManagement() {
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [showDialog, setShowDialog] = useState(false)

  useEffect(() => {
    loadMessages()
  }, [])

  const loadMessages = async () => {
    try {
      setLoading(true)
      const response = await contactAPI.getAll()
      setMessages(response.data)
      setError('')
    } catch (err) {
      setError('Fehler beim Laden der Nachrichten')
      logger.error('Error loading messages:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleMessageClick = async (message) => {
    setSelectedMessage(message)
    setShowDialog(true)

    // Nachricht als gelesen markieren, falls noch nicht gelesen
    if (!message.is_read) {
      try {
        await contactAPI.update(message.id, { is_read: true })
        // Liste aktualisieren
        setMessages(messages.map(m =>
          m.id === message.id ? { ...m, is_read: true } : m
        ))
      } catch (err) {
        logger.error('Error marking message as read:', err)
      }
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diese Nachricht wirklich löschen?')) {
      return
    }

    try {
      await contactAPI.delete(id)
      setMessages(messages.filter(m => m.id !== id))
      setShowDialog(false)
      setSelectedMessage(null)
    } catch (err) {
      alert('Fehler beim Löschen der Nachricht')
      logger.error('Error deleting message:', err)
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (now - date) / (1000 * 60 * 60)

    if (diffInHours < 24) {
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    } else if (diffInHours < 48) {
      return 'Gestern'
    } else {
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    }
  }

  const unreadCount = messages.filter(m => !m.is_read).length

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>Kontaktanfragen</h1>
        {unreadCount > 0 && (
          <p className="wc-muted" style={{ fontSize: 13, margin: 0 }}>
            {unreadCount} ungelesene {unreadCount === 1 ? 'Nachricht' : 'Nachrichten'}
          </p>
        )}
      </div>

      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {messages.length === 0 ? (
        <div className="wc-card wc-elev-sm" style={{ textAlign: 'center', padding: '48px 0' }}>
          <p className="wc-muted" style={{ margin: 0, fontSize: 15 }}>Keine Nachrichten vorhanden</p>
        </div>
      ) : (
        <div className="wc-card wc-elev-sm" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="wc-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Status</th>
                  <th>Von</th>
                  <th>E-Mail</th>
                  <th>Betreff</th>
                  <th style={{ width: 120 }}>Datum</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((message) => (
                  <tr
                    key={message.id}
                    onClick={() => handleMessageClick(message)}
                    style={{
                      cursor: 'pointer',
                      background: !message.is_read ? 'color-mix(in srgb, var(--wc-accent) 8%, transparent)' : undefined,
                    }}
                  >
                    <td>
                      {!message.is_read && <span className="wc-tag wc-tag-accent">NEU</span>}
                    </td>
                    <td style={{ fontWeight: !message.is_read ? 600 : 400 }}>{message.name}</td>
                    <td style={{ fontWeight: !message.is_read ? 600 : 400 }}>{message.email}</td>
                    <td style={{ fontWeight: !message.is_read ? 600 : 400 }}>
                      <div style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {message.subject}
                      </div>
                    </td>
                    <td className="wc-muted">{formatDate(message.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Message Dialog */}
      {showDialog && selectedMessage && (
        <div className="wc-modal-overlay" onClick={() => setShowDialog(false)}>
          <div className="wc-modal wc-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 className="wc-card-title" style={{ margin: 0 }}>{selectedMessage.subject}</h3>
                <div className="wc-muted" style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                  <p style={{ margin: 0 }}><strong style={{ color: 'var(--wc-text)' }}>Von:</strong> {selectedMessage.name}</p>
                  <p style={{ margin: 0 }}>
                    <strong style={{ color: 'var(--wc-text)' }}>E-Mail:</strong>{' '}
                    <a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a>
                  </p>
                  {selectedMessage.phone && (
                    <p style={{ margin: 0 }}>
                      <strong style={{ color: 'var(--wc-text)' }}>Telefon:</strong>{' '}
                      <a href={`tel:${selectedMessage.phone}`}>{selectedMessage.phone}</a>
                    </p>
                  )}
                  <p style={{ margin: 0 }}>
                    <strong style={{ color: 'var(--wc-text)' }}>Datum:</strong>{' '}
                    {new Date(selectedMessage.created_at).toLocaleString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowDialog(false)}
                className="wc-btn wc-btn-icon wc-btn-secondary"
                aria-label="Schließen"
              >
                ×
              </button>
            </div>

            <div>
              <h4 className="wc-muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', margin: '0 0 8px' }}>
                Nachricht
              </h4>
              <div
                style={{
                  background: 'color-mix(in srgb, var(--wc-text) 6%, transparent)',
                  borderRadius: 'var(--wc-radius-md)',
                  padding: 14,
                  whiteSpace: 'pre-wrap',
                  fontSize: 14,
                }}
              >
                {selectedMessage.message}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px solid var(--wc-divider)' }}>
              <button
                onClick={() => handleDelete(selectedMessage.id)}
                className="wc-btn wc-btn-secondary"
                style={{ color: '#fca5a5' }}
              >
                Löschen
              </button>
              <div style={{ display: 'flex', gap: 12 }}>
                <a
                  href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                  className="wc-btn wc-btn-primary"
                >
                  Antworten
                </a>
                <button onClick={() => setShowDialog(false)} className="wc-btn wc-btn-secondary">
                  Schließen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
